// Image caching utility to reduce S3 egress costs
const IMAGE_CACHE_PREFIX = 'image_cache_';
const IMAGE_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Get cached image from localStorage or return null if not found/expired
export const getCachedImage = (url) => {
  if (!url) return null;
  
  try {
    // Create a simple hash of the URL to use as the cache key
    // We don't need the query parameters as they change with pre-signed URLs
    const urlWithoutParams = url.split('?')[0];
    const cacheKey = `${IMAGE_CACHE_PREFIX}${urlWithoutParams}`;
    
    const cachedData = localStorage.getItem(cacheKey);
    if (!cachedData) return null;
    
    const { timestamp, data } = JSON.parse(cachedData);
    
    // Check if the cached image has expired
    if (Date.now() - timestamp > IMAGE_CACHE_EXPIRY) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error retrieving cached image:', error);
    return null;
  }
};

// Cache image data to localStorage
export const cacheImage = async (url) => {
  if (!url) return null;
  
  try {
    // Create a cache key from the URL without query parameters
    const urlWithoutParams = url.split('?')[0];
    const cacheKey = `${IMAGE_CACHE_PREFIX}${urlWithoutParams}`;
    
    // Check if we already have this image cached
    const existingCache = getCachedImage(url);
    if (existingCache) return existingCache;
    
    // Fetch the image and convert to base64
    const response = await fetch(url);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Store the base64 data in localStorage
        const base64data = reader.result;
        localStorage.setItem(cacheKey, JSON.stringify({
          timestamp: Date.now(),
          data: base64data
        }));
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error caching image:', error);
    return null;
  }
};

// Helper function to get an image (from cache if available, otherwise fetch and cache)
export const getImage = async (url) => {
  if (!url) return null;
  
  // Try to get from cache first
  const cachedImage = getCachedImage(url);
  if (cachedImage) {
    return cachedImage;
  }
  
  // If not in cache, fetch and cache it in a separate process
  // This prevents blocking the UI when fetching an uncached image
  setTimeout(() => {
    cacheImage(url).catch(err => {
      console.error('Background image caching error:', err);
    });
  }, 0);
  
  // Return null for now, forcing components to use the fallback avatar
  // The component will use the cached version on the next render
  return null;
};
